/**
 * ChatMessage Component
 * Renders a single chat message bubble with user/assistant styling.
 * Assistant messages include thumbs up/down feedback buttons.
 */

import React from 'react';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon, HandThumbDownIcon as HandThumbDownSolidIcon } from '@heroicons/react/24/solid';
import type { ChatMessage as ChatMessageType } from './useAIChat';

interface ChatMessageProps {
  message: ChatMessageType;
  onRate?: (messageId: string, rating: 'thumbs_up' | 'thumbs_down') => void;
  /** "Check again" for a bubble whose stream was cut before the answer arrived. */
  onCheckAgain?: (messageId: string) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, onRate, onCheckAgain }) => {
  const isUser = message.role === 'user';
  const showFeedback = !isUser && !message.isError && !message.streaming && !message.needsRecheck && message.messageIndex !== undefined && onRate;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-blue-600 text-white'
            : message.isError
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-white text-gray-800 border border-gray-200 shadow-sm'
        }`}
      >
        {/* Message content with basic markdown-like formatting */}
        <div className="whitespace-pre-wrap break-words">
          {formatContent(message.content)}
          {message.streaming && message.content && (
            <span className="inline-block w-1.5 h-4 ml-0.5 align-text-bottom bg-gray-400 animate-pulse" aria-hidden="true" />
          )}
        </div>

        {/* Running status while the answer is being worked out */}
        {message.streaming && message.status && (
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500" role="status">
            <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            <span>{message.status}</span>
          </div>
        )}

        {/* The stream was cut: the answer is saved server-side; offer a reload */}
        {message.needsRecheck && onCheckAgain && (
          <button
            onClick={() => onCheckAgain(message.id)}
            className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            Check again
          </button>
        )}

        {/* Tools used indicator */}
        {message.toolsUsed && message.toolsUsed.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1 text-xs text-gray-400">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125v-3.75" />
            </svg>
            Queried {message.toolsUsed.length} data source{message.toolsUsed.length > 1 ? 's' : ''}
          </div>
        )}

        {/* Feedback buttons */}
        {showFeedback && (
          <div className={`mt-2 pt-2 flex items-center gap-1 ${
            message.toolsUsed && message.toolsUsed.length > 0 ? '' : 'border-t border-gray-100'
          }`}>
            <button
              onClick={() => onRate(message.id, 'thumbs_up')}
              className={`p-1 rounded transition-colors ${
                message.rating === 'thumbs_up'
                  ? 'text-green-600'
                  : 'text-gray-300 hover:text-gray-500'
              }`}
              title="Helpful"
            >
              {message.rating === 'thumbs_up'
                ? <HandThumbUpSolidIcon className="w-3.5 h-3.5" />
                : <HandThumbUpIcon className="w-3.5 h-3.5" />
              }
            </button>
            <button
              onClick={() => onRate(message.id, 'thumbs_down')}
              className={`p-1 rounded transition-colors ${
                message.rating === 'thumbs_down'
                  ? 'text-red-500'
                  : 'text-gray-300 hover:text-gray-500'
              }`}
              title="Not helpful"
            >
              {message.rating === 'thumbs_down'
                ? <HandThumbDownSolidIcon className="w-3.5 h-3.5" />
                : <HandThumbDownIcon className="w-3.5 h-3.5" />
              }
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Basic content formatting - handles bold markers and currency
 */
function formatContent(content: string): string {
  return content;
}
